import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './experience.css';

const Experience: React.FC = () => {
  const { pathname } = useLocation();
  const [toggleState, setToggleState] = useState(pathname === '/education' ? 2 : 1);
  const [isVisible, setIsVisible] = useState(false);

  // Keep the tab in sync with the URL so navigating between /experience and /education works.
  useEffect(() => {
    if (pathname === '/education') {
      setToggleState(2);
    } else if (pathname === '/experience') {
      setToggleState(1);
    }
  }, [pathname]);

  const toggleTab = (index: number) => {
    setToggleState(index);
  };

  // Add scroll animation
  useEffect(() => {
    const handleScroll = () => {
      const experienceSection = document.getElementById('experience');
      if (experienceSection) {
        const sectionPosition = experienceSection.getBoundingClientRect();
        const screenPosition = window.innerHeight;
        
        if (sectionPosition.top < screenPosition * 0.75) {
          setIsVisible(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on initial load
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <section className={`experience section ${isVisible ? 'animate' : ''}`} id="experience">
        <div className="section__header">
          <h2 className="section__title">Experience</h2>
          <span className="section__subtitle">My journey</span>
        </div>

        <div className="experience__container container">
          <div className="experience__tabs">
            <div 
              className={
                toggleState === 1 
                  ? "experience__button experience__active button--flex" 
                  : "experience__button button--flex"
              }
              onClick={() => toggleTab(1)}
            >
              <i className="uil uil-briefcase-alt experience__icon"></i> Work
            </div>

            <div 
              className={
                toggleState === 2 
                  ? "experience__button experience__active button--flex" 
                  : "experience__button button--flex"
              }
              onClick={() => toggleTab(2)}
            >
              <i className="uil uil-graduation-cap experience__icon"></i> Education
            </div>
          </div>

          <div className="experience__sections">
            {/* Work Experience */}
            <div className={toggleState === 1 ? "experience__content experience__content-active" : "experience__content"}>
              <div className="experience__data">
                <div>
                  <h3 className="experience__title">Software Contributor</h3>
                  <span className="experience__subtitle">
                    <img 
                      src="/assets/company-logos/google-2015-seeklogo-removebg-preview.png" 
                      alt="Google Logo" 
                      className="experience__company-logo"
                    />
                    Google DeepMind
                  </span>
                  <div className="experience__calendar">
                    <i className="uil uil-calendar-alt"></i> June - October 2025
                  </div>
                  <ul className="experience__description">
                    <li>Collaborated with the Vertex AI team to improve reference-based context management for enterprise-scale Agent Development Kit (ADK) applications, reframing 1-2M token context windows as a multi-tier storage and retrieval optimization problem.</li>
                    <li>Designed and benchmarked a content-aware compression pipeline across six algorithms (LZ4, ZLIB, LZMA, BZIP2, ZSTD, Snappy) and five multimodal content types, identifying optimal compression strategies that improved throughput for large agent workflows.</li>
                    <li>Built a hybrid hash-based deduplication protocol using MD5 for text references and SHA256 for multimodal binaries, enabling reuse of intermediate artifacts across distributed agent systems and reducing storage requirements by 99.55% with zero data loss.</li>
                    <li>Implemented a multi-tier caching architecture (LRU/LFU/TTL) with memory-pressure monitoring and disk spillover, reducing serialization overhead and improving stability in distributed agent execution pipelines.</li>
                  </ul>
                </div>

                <div>
                  <span className="experience__rounder"></span>
                  <span className="experience__line"></span>
                </div>
              </div>

              <div className="experience__data">
                <div></div>

                <div>
                  <span className="experience__rounder"></span>
                  <span className="experience__line"></span>
                </div>

                <div>
                  <h3 className="experience__title">Software Engineering Intern</h3>
                  <span className="experience__subtitle">
                    <img 
                      src="/assets/company-logos/chase-bank.png" 
                      alt="JPMorgan Chase Logo" 
                      className="experience__company-logo"
                    />
                    JPMorgan Chase
                  </span>
                  <div className="experience__calendar">
                    <i className="uil uil-calendar-alt"></i> January - May 2025
                  </div>
                  <ul className="experience__description">
                    <li>Architected distributed middleware using <strong>Java Spring Boot</strong> and <strong>Apache Kafka</strong> to decouple legacy Core Payment Systems, implementing RESTful APIs and advanced algorithms, collaborating with 9+ teams to reduce migration effort by 93%.</li>
                    <li>Engineered enterprise microservice architecture on <strong>AWS EC2</strong> with CI/CD pipelines, implementing circuit breaker patterns and <strong>SQL</strong> optimization for Zelle and wire systems, achieving sub-100ms latency and 99.9% uptime.</li>
                  </ul>
                </div>
              </div>

              <div className="experience__data">
                <div>
                  <h3 className="experience__title">Machine Learning Engineer</h3>
                  <span className="experience__subtitle">
                    <img 
                      src="/assets/company-logos/ohio-state-university-seal-seeklogo-removebg-preview.png" 
                      alt="Ohio State University Logo" 
                      className="experience__company-logo"
                    />
                    Buckeye Autodrive, Ohio State University
                  </span>
                  <div className="experience__calendar">
                    <i className="uil uil-calendar-alt"></i> August 2024 - January 2025
                  </div>
                  <ul className="experience__description">
                    <li>Developed <strong>PyTorch</strong> CNN implementing algorithms and data structures in <strong>Python</strong> for real-time traffic sign recognition using transfer learning, achieving 99.2% accuracy and reducing processing time from minutes to milliseconds.</li>
                    <li>Engineered sensor fusion pipeline integrating LiDAR with camera data using advanced data structures, implementing Kalman filtering APIs and improving object detection accuracy by 18%.</li>
                  </ul>
                </div>

                <div>
                  <span className="experience__rounder"></span>
                  <span className="experience__line"></span>
                </div>
              </div>

              <div className="experience__data">
                <div></div>

                <div>
                  <span className="experience__rounder"></span>
                  <span className="experience__line"></span>
                </div>

                <div>
                  <div className="experience__title-container">
                    <h3 className="experience__title">Software Engineer</h3>
                    <a 
                      href="https://www.paws-ai.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="experience__project-button"
                    >
                      View Work <i className="uil uil-arrow-right"></i>
                    </a>
                  </div>
                  <span className="experience__subtitle">
                    <i className="uil uil-desktop experience__company-icon"></i>
                    PawsAI (Remote)
                  </span>
                  <div className="experience__calendar">
                    <i className="uil uil-calendar-alt"></i> March - August 2024
                  </div>
                  <ul className="experience__description">
                    <li>Architected two-tiered caching system combining LRU cache and <strong>Redis</strong> in <strong>Python</strong>, implementing <strong>SQL</strong> optimization for vector embeddings and <strong>PostgreSQL</strong> patient data, reducing database queries by 65%.</li>
                    <li>Built asynchronous WebSocket system using <strong>FastAPI</strong> with connection pooling and <strong>Firebase</strong> authentication, achieving 300+ simultaneous connections with sub-200ms latency.</li>
                  </ul>
                </div>
              </div>

              <div className="experience__data">
                <div>
                  <h3 className="experience__title">Software Engineering Intern</h3>
                  <span className="experience__subtitle">
                    <i className="uil uil-mobile-android experience__company-icon"></i>
                    Yinsight (Columbus, OH)
                  </span>
                  <div className="experience__calendar">
                    <i className="uil uil-calendar-alt"></i> August 2023 - January 2024
                  </div>
                  <ul className="experience__description">
                    <li>Developed cross-platform mobile app using <strong>Dart/Swift</strong> with MVVM architecture, implementing algorithms for active recall learning and Google Calendar synchronization, increasing user engagement by 32%.</li>
                    <li>Redesigned database schema and <strong>SQL</strong> query patterns for improved entity relationships, implementing automated testing and CI/CD pipelines, reducing content delivery latency by 30%.</li>
                  </ul>
                </div>

                <div>
                  <span className="experience__rounder"></span>
                </div>
              </div>
            </div>

            {/* Education */}
            <div className={toggleState === 2 ? "experience__content experience__content-active" : "experience__content"}>
              <div className="experience__data">
                <div>
                  <h3 className="experience__title">Master of Science in Computer Science</h3>
                  <span className="experience__subtitle">
                    <img 
                      src="/assets/company-logos/GTech.webp?v=20260309" 
                      alt="Georgia Institute of Technology Logo" 
                      className="experience__company-logo"
                    />
                    Georgia Institute of Technology
                  </span>
                  <ul className="experience__description">
                    <li><strong>GPA: 4.0/4.0</strong></li>
                  </ul>
                </div>

                <div>
                  <span className="experience__rounder"></span>
                  <span className="experience__line"></span>
                </div>
              </div>

              <div className="experience__data">
                <div></div>

                <div>
                  <span className="experience__rounder"></span>
                  <span className="experience__line"></span>
                </div>

                <div>
                  <h3 className="experience__title">Bachelor of Science in Computer Science and Engineering</h3>
                  <span className="experience__subtitle">
                    <img 
                      src="/assets/company-logos/ohio-state-university-seal-seeklogo-removebg-preview.png" 
                      alt="Ohio State University Logo" 
                      className="experience__company-logo"
                    />
                    The Ohio State University
                  </span>
                  <div className="experience__calendar">
                    <i className="uil uil-calendar-alt"></i> Completed August 2025
                  </div>
                  <ul className="experience__description">
                    <li><strong>GPA: 3.6</strong> | Awards: Dean's List, Excellence Fellowship In Computing Scholarship Award</li>
                    <li>Relevant Coursework: Neural Networks, Data Structures and Algorithms, Operating Systems, Relational Database</li>
                  </ul>
                </div>
              </div>

              <div className="experience__data">
                <div>
                  <h3 className="experience__title">Certificate in Machine Learning</h3>
                  <span className="experience__subtitle">
                    <img 
                      src="/assets/company-logos/cornell-university-seeklogo-removebg-preview.png" 
                      alt="Cornell University Logo" 
                      className="experience__company-logo"
                    />
                    Cornell University (Remote)
                  </span>
                  <div className="experience__calendar">
                    <i className="uil uil-calendar-alt"></i> Completed August 2025
                  </div>
                  <ul className="experience__description">
                    <li>Break Through Tech AI Program - Selective program focusing on NLP, Deep Learning, Computer Vision, and LLMs.</li>
                  </ul>
                </div>

                <div>
                  <span className="experience__rounder"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Experience;
